import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import CompetitionPopup from './CompetitionPopup';
import NotificationBell from './NotificationBell';
import { MapPin, Menu, X, User, LogOut, CalendarDays, Inbox } from 'lucide-react';
import WhatsAppButton from './WhatsAppButton';
import InstallPWABanner from './InstallPWABanner';
// Cookie-Banner wird jetzt zentral über das InvictaFlow-Loader-Script in index.html geladen.
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Layout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { user, isAuthenticated, isStaff, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  // Schließe Mobile-Menü bei Route-Wechsel
  React.useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Schließe Mobile-Menü mit Escape
  React.useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setMobileMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileMenuOpen]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const navLinks = [
    { path: '/locations', label: t('nav.locations') },
    { path: '/mockup-generator', label: 'Mockup' },
    { path: '/blog', label: 'Blog' },
    { path: '/wettbewerb', label: 'Wettbewerb' },
    ...(isStaff ? [{ path: '/admin', label: t('nav.admin') }] : []),
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <CompetitionPopup />
      {/* Header */}
      <header className="bg-white border-b border-[hsl(var(--border))] sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <div className="w-10 h-10 bg-[hsl(var(--primary))] rounded-lg flex items-center justify-center">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <span className="font-semibold text-lg hidden sm:block">ZO Werbefläche</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
                  data-testid={`nav-${link.path.slice(1) || 'home'}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-4">
              {/* Language Switcher */}
              <div className="lang-switch hidden sm:flex">
                {['de', 'en', 'fr', 'it'].map((lng) => (
                  <button
                    key={lng}
                    onClick={() => changeLanguage(lng)}
                    className={`lang-btn ${i18n.language === lng ? 'active' : ''}`}
                    data-testid={`lang-${lng}`}
                  >
                    {lng}
                  </button>
                ))}
              </div>

              {/* User Menu */}
              {isAuthenticated ? (
                <>
                  <NotificationBell />
                  <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="hidden sm:flex" data-testid="user-menu-trigger">
                      <User className="w-4 h-4 mr-2" />
                      {user?.name?.split(' ')[0]}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link to="/my-bookings" className="flex items-center" data-testid="user-menu-bookings">
                        <CalendarDays className="w-4 h-4 mr-2" />
                        Meine Buchungen
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/portal" className="flex items-center" data-testid="user-menu-portal">
                        <Inbox className="w-4 h-4 mr-2" />
                        Kundenbereich
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="text-[hsl(var(--destructive))]" data-testid="user-menu-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      Abmelden
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                </>
              ) : (
                <Link to="/auth">
                  <Button variant="outline" size="sm" className="hidden sm:flex" data-testid="nav-login">
                    <User className="w-4 h-4 mr-2" />
                    Anmelden
                  </Button>
                </Link>
              )}

              {/* CTA Button */}
              <Link to="/locations">
                <Button className="hidden sm:flex" data-testid="nav-cta">
                  {t('nav.findLocation')}
                </Button>
              </Link>

              {/* Mobile Menu Button */}
              <button
                className="md:hidden p-3 min-h-[44px] min-w-[44px] flex items-center justify-center"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Menü öffnen"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[hsl(var(--border))]">
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link py-2 ${location.pathname === link.path ? 'active' : ''}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              {isAuthenticated ? (
                <>
                  <Link to="/my-bookings" className="nav-link py-2" onClick={() => setMobileMenuOpen(false)}>
                    Meine Buchungen
                  </Link>
                  <Link to="/portal" className="nav-link py-2" onClick={() => setMobileMenuOpen(false)}>
                    Kundenbereich
                  </Link>
                  <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="nav-link py-2 text-left text-[hsl(var(--destructive))]">
                    Abmelden
                  </button>
                </>
              ) : (
                <Link to="/auth" className="nav-link py-2" onClick={() => setMobileMenuOpen(false)}>
                  Anmelden
                </Link>
              )}
              <div className="lang-switch pt-2 border-t border-[hsl(var(--border))]">
                {['de', 'en', 'fr', 'it'].map((lng) => (
                  <button
                    key={lng}
                    onClick={() => changeLanguage(lng)}
                    className={`lang-btn ${i18n.language === lng ? 'active' : ''}`}
                  >
                    {lng}
                  </button>
                ))}
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[hsl(var(--foreground))] text-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-[hsl(var(--primary))] rounded-lg flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <span className="font-semibold text-lg">ZO Werbefläche</span>
              </div>
              <p className="text-gray-400 max-w-md">
                {t('footer.description')}
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4">{t('footer.quickLinks')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/locations" className="hover:text-white transition-colors">{t('nav.locations')}</Link></li>
                <li><Link to="/#how-it-works" className="hover:text-white transition-colors">{t('nav.howItWorks')}</Link></li>
                {isAuthenticated && (
                  <li><Link to="/my-bookings" className="hover:text-white transition-colors">Meine Buchungen</Link></li>
                )}
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-semibold mb-4">{t('footer.legal')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/terms" className="hover:text-white transition-colors">{t('footer.terms')}</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">{t('footer.privacy')}</Link></li>
                <li><Link to="/imprint" className="hover:text-white transition-colors">{t('footer.imprint')}</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-500 text-sm space-y-3">
            <div>{t('footer.copyright')}</div>
            <div className="text-xs text-gray-500">
              Softwareplattform und digitale Infrastruktur entwickelt, betrieben und lizenziert durch{' '}
              <a
                href="https://www.invictaflow.ch"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white underline underline-offset-2"
                data-testid="footer-invictax-link"
              >
                InvictaX Group GmbH
              </a>
              .
            </div>
          </div>
        </div>
      </footer>
      <WhatsAppButton />
      <InstallPWABanner />
      {/* Cookie-Banner läuft zentral über InvictaFlow-Loader (siehe index.html) */}
    </div>
  );
};

export default Layout;
